import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Section } from "./_shared";

export default function BillingPanel({ brand, role }) {  // eslint-disable-line no-unused-vars
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isOwner = brand?.owner_user_id === user?.user_id;
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/brands/${brand.id}/plan`);
      setPlan(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [brand.id]);

  if (!isOwner) {
    return (
      <div className="relative" data-testid="billing-locked">
        <div className="pointer-events-none select-none blur-md opacity-60" aria-hidden="true">
          <Section title={t("settings.billing_title")} subtitle={t("settings.billing_sub")} action={null}>
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 py-14" />
          </Section>
        </div>
        <div className="absolute inset-0 flex items-start justify-center pt-16">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-100 p-6">
            <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center mb-4">
              <Lock className="w-5 h-5 text-slate-700" strokeWidth={1.75} />
            </div>
            <h3 className="text-lg font-medium text-slate-900 tracking-tight">Restricted Access</h3>
            <p className="text-sm text-slate-500 mt-1.5">{t("settings.billing_restricted")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !plan) {
    return (
      <Section title={t("settings.billing_title")} subtitle={t("settings.billing_sub")} action={null}>
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 py-14 text-center text-gray-500 text-sm">
          {t("common.loading") || "Loading…"}
        </div>
      </Section>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { day: "numeric", month: "long", year: "numeric" });
    } catch { return null; }
  };

  const planName = plan.active ? t("settings.billing_plan_pro") : (plan.credits > 0 ? t("settings.billing_plan_credits") : t("settings.billing_plan_free"));
  const planBadgeClass = plan.active
    ? "bg-slate-900 text-white"
    : plan.credits > 0 ? "bg-amber-100 text-amber-900" : "bg-gray-100 text-gray-700";
  const renewalDate = fmtDate(plan.current_period_end);
  const renewalLabel = plan.cancel_at_period_end
    ? t("settings.billing_cancels_on")
    : (plan.active ? t("settings.billing_renewal") : t("settings.billing_expires"));

  const openCheckout = async (lookup_key) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/checkout`, {
        lookup_key, brand_id: brand.id, origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout mislukt");
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/brands/${brand.id}/cancel-subscription`);
      toast.success(t("settings.billing_canceled_ok"));
      setConfirming(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opzeggen mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Section title={t("settings.billing_title")} subtitle={t("settings.billing_sub")} action={null}>
      {/* Current plan card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="billing-plan-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">{t("settings.billing_plan_label")}</div>
            <div className="mt-1 flex items-center gap-2">
              <span data-testid="billing-plan-name" className={`inline-flex items-center px-2.5 py-1 rounded-md text-[13px] font-medium ${planBadgeClass}`}>
                {planName}
              </span>
              {plan.cancel_at_period_end && (
                <span data-testid="billing-cancel-pending" className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5">
                  {t("settings.billing_cancel_pending")}
                </span>
              )}
            </div>
            {renewalDate && (plan.active || plan.cancel_at_period_end) && (
              <div className="mt-3 text-sm text-slate-700" data-testid="billing-renewal-date">
                {renewalLabel} <strong className="font-medium">{renewalDate}</strong>
              </div>
            )}
            {!plan.active && plan.credits === 0 && (
              <p className="mt-3 text-sm text-gray-500 max-w-md">
                {t("settings.billing_free_body")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {plan.active && !plan.cancel_at_period_end && (
              <Button
                onClick={() => setConfirming(true)}
                data-testid="billing-cancel-btn"
                className="h-10 rounded-md bg-white border border-red-200 text-red-700 hover:bg-red-50 shadow-none"
              >
                {t("settings.billing_cancel")}
              </Button>
            )}
            {!plan.active && (
              <Button
                onClick={() => openCheckout("brand_pro_monthly")}
                disabled={busy}
                data-testid="billing-upgrade-btn"
                className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800"
              >
                {t("settings.billing_upgrade")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Credits */}
      <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="billing-credits-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">{t("settings.billing_credits_label")}</div>
            <div className="mt-1 text-3xl font-display font-medium text-slate-900" data-testid="billing-credits-value">{plan.credits}</div>
            <p className="mt-1 text-sm text-gray-500 max-w-md">{t("settings.billing_credits_desc")}</p>
          </div>
          <Button
            onClick={() => openCheckout("brand_credits_50")}
            disabled={busy}
            data-testid="billing-buy-credits-btn"
            className="h-10 rounded-md bg-white border border-gray-200 text-slate-900 hover:bg-gray-50 shadow-none"
          >
            {t("settings.billing_buy_credits")}
          </Button>
        </div>
      </div>

      {/* Cancel confirmation */}
      {confirming && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 space-y-3" data-testid="billing-cancel-confirm">
          <p className="text-sm text-amber-900">{t("settings.billing_cancel_confirm")}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setConfirming(false)}
              className="h-9 rounded-md bg-white border border-gray-200 text-slate-900 hover:bg-gray-50 shadow-none"
              data-testid="billing-cancel-dismiss"
            >
              {t("profile_page.cancel")}
            </Button>
            <Button
              onClick={doCancel}
              disabled={busy}
              data-testid="billing-cancel-confirm-btn"
              className="h-9 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            >
              {busy ? t("settings.billing_canceling") : t("settings.billing_cancel")}
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}
